import React, { useState } from 'react';
import { B } from '../constants';
import Input from './ui/Input';
import { promos as promosAPI } from '../api';

const PRESETS = [
  { value: 'low', label: 'Low', icon: '⚡', desc: 'Fast upload, smaller file — good for slow connections' },
  { value: 'medium', label: 'Medium', icon: '⚖️', desc: 'Balanced quality and file size — recommended' },
  { value: 'high', label: 'High', icon: '✨', desc: 'Better quality, larger file — best for most platforms' },
  { value: 'ultra', label: 'Ultra', icon: '💎', desc: 'Maximum quality — for professional content' },
];

const CODECS = [
  { value: 'h264', label: 'H.264', icon: '✅', desc: 'Universal compatibility — works everywhere' },
  { value: 'h265', label: 'H.265', icon: '🔄', desc: 'Modern codec — Instagram, Facebook, YouTube' },
  { value: 'vp9', label: 'VP9', icon: '🌐', desc: 'Web-optimized — Twitter, YouTube' },
  { value: 'av1', label: 'AV1', icon: '🔮', desc: 'Cutting-edge — limited support, future-proof' },
];

export default function QualitySettings({ value, onChange, durationSecs }) {
  const [customBitrate, setCustomBitrate] = useState(value.customBitrate || '');
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);

  const handlePresetChange = (preset) => {
    onChange({ ...value, preset, customBitrate: '' });
    setCustomBitrate('');
  };

  const handleCodecChange = (codec) => {
    onChange({ ...value, codec });
    setValidation(null);
  };

  const handleCustomBitrate = (val) => {
    setCustomBitrate(val);
    onChange({ ...value, customBitrate: val || null });
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      const result = await promosAPI.validateQuality({
        preset: value.preset,
        codec: value.codec,
        customBitrate: value.customBitrate || undefined,
        platforms: ['insta_reel', 'fb_reel', 'twitter', 'yt_shorts'],
      });
      setValidation(result);
    } catch {
      setValidation(null);
    }
    setValidating(false);
  };

  const selectedPreset = PRESETS.find(p => p.value === (value.preset || 'medium'));
  const selectedCodec = CODECS.find(c => c.value === (value.codec || 'h264'));

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: B.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
        Quality Settings
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: B.muted, marginBottom: 6 }}>Quality Preset</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {PRESETS.map(p => (
            <button key={p.value} onClick={() => handlePresetChange(p.value)}
              style={{
                padding: '10px 12px', borderRadius: 10, border: `2px solid ${(value.preset || 'medium') === p.value ? B.forest : '#e5e0d8'}`,
                background: (value.preset || 'medium') === p.value ? 'rgba(26,107,74,0.08)' : 'white',
                cursor: 'pointer', textAlign: 'left', fontFamily: "'Montserrat',sans-serif", transition: 'all 0.15s',
              }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: (value.preset || 'medium') === p.value ? B.forest : B.night }}>
                {p.icon} {p.label}
              </div>
              <div style={{ fontSize: 10, color: B.muted, marginTop: 2 }}>{p.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: B.muted, marginBottom: 6 }}>Codec</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {CODECS.map(c => (
            <button key={c.value} onClick={() => handleCodecChange(c.value)}
              style={{
                flex: 1, padding: '8px 6px', borderRadius: 8, border: `2px solid ${(value.codec || 'h264') === c.value ? B.forest : '#e5e0d8'}`,
                background: (value.codec || 'h264') === c.value ? 'rgba(26,107,74,0.08)' : 'white',
                cursor: 'pointer', textAlign: 'center', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600,
                color: (value.codec || 'h264') === c.value ? B.forest : B.muted,
              }}>
              <div style={{ fontSize: 14 }}>{c.icon}</div>
              <div>{c.label}</div>
            </button>
          ))}
        </div>
        {selectedCodec && (
          <p style={{ fontSize: 10, color: B.muted, marginTop: 4, fontFamily: "'Montserrat',sans-serif" }}>
            {selectedCodec.desc}
          </p>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: B.muted, marginBottom: 4 }}>Custom Bitrate (optional)</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <Input type="text" placeholder="e.g. 5000k, 10M"
              value={customBitrate}
              onChange={e => handleCustomBitrate(e.target.value)}
              size="sm" />
          </div>
          <button onClick={handleValidate} disabled={validating}
            style={{
              padding: '8px 14px', borderRadius: 8, border: 'none', background: B.forest,
              color: 'white', cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
            }}>
            {validating ? '...' : 'Validate'}
          </button>
        </div>
      </div>

      {validation && (
        <div style={{ marginBottom: 12 }}>
          {validation.errors?.length > 0 && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(220,53,69,0.08)', marginBottom: 6 }}>
              {validation.errors.map((err, i) => (
                <p key={i} style={{ fontSize: 11, color: '#dc3545', margin: 0 }}>❌ {err}</p>
              ))}
            </div>
          )}
          {validation.warnings?.length > 0 && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(245,166,35,0.1)' }}>
              {validation.warnings.map((w, i) => (
                <p key={i} style={{ fontSize: 11, color: '#8a6d00', margin: 0 }}>⚠️ {w.message}</p>
              ))}
            </div>
          )}
          {validation.valid && validation.warnings?.length === 0 && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(26,107,74,0.08)', fontSize: 11, color: B.forest }}>
              ✅ Settings are compatible with all platforms
            </div>
          )}
        </div>
      )}

      <div style={{ padding: '10px 14px', borderRadius: 10, background: B.sand, fontSize: 11, color: B.muted, lineHeight: 1.5 }}>
        <strong style={{ color: B.night }}>Quality Preview</strong><br />
        Preset: <strong>{selectedPreset?.label || 'Medium'}</strong> · Codec: <strong>{selectedCodec?.label || 'H.264'}</strong>
        {value.customBitrate && <span> · Custom: <strong>{value.customBitrate}</strong></span>}
        <br />
        {selectedPreset && (
          <span>Est. quality: {selectedPreset.value === 'ultra' ? '💎 Maximum' : selectedPreset.value === 'high' ? '✨ High' : selectedPreset.value === 'low' ? '⚡ Basic' : '⚖️ Balanced'}</span>
        )}
      </div>
    </div>
  );
}
